import { getAnalytics } from "@tm/insurance/analytics";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import { ICarouselPersuasionData } from "../../pages/InsuranceCommonLanding/types";
import DropOffModal from "../DropOffModal/DropOffModal";
import FormattedText from "../FormattedText/FormattedText";
import { ImageComponent } from "../ImageComponent/ImageComponent";
import {
  CarouselItemStyle,
  CarouselPersuasionStyle,
  ImageContainer,
} from "./CarouselPersuasion.style";

const CarouselPersuasion = ({
  carouselPersuasionData,
}: {
  carouselPersuasionData: ICarouselPersuasionData[];
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [showDropOffModal, setShowDropOffModal] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackCommonLandingEvents({
        actionType: "value_prop_animation_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: carouselPersuasionRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5, // Trigger when 50% of component is visible
      triggerOnLeaveViewport: true,
    }
  );

  useEffect(() => {
    // Only rotate if there's more than 1 item
    if (carouselPersuasionData.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setIsAnimating(false);

      setTimeout(() => {
        setCurrentIndex(
          (prevIndex) => (prevIndex + 1) % carouselPersuasionData.length
        );
        setIsAnimating(true);
      }, 100); // Short delay for fade out before changing content
    }, 3000);

    return () => clearInterval(interval);
  }, [carouselPersuasionData.length]);

  const currentItem = carouselPersuasionData[currentIndex];

  const handleRightIconClick = (item: ICarouselPersuasionData) => {
    analytics?.trackCommonLandingEvents({
      actionType: `value_prop_${currentIndex}_animation_clicked`,
    });
    if (item.url) {
      window.open(item.url, "_blank");
    }
    if (item.dropOffBottomSheet) {
      setModalData(item.dropOffBottomSheet);
      setShowDropOffModal(true);
    }
  };

  return (
    <>
      <CarouselPersuasionStyle ref={carouselPersuasionRef}>
        <CarouselItemStyle isAnimating={isAnimating}>
          {currentItem?.leftIcon && (
            <ImageComponent
              src={currentItem.leftIcon}
              width="20px"
              height="20px"
              alt={"leftIcon"}
            />
          )}
          <FormattedText value={currentItem?.title} />
          {currentItem?.rightIcon && (
            <ImageContainer onClick={() => handleRightIconClick(currentItem)}>
              <ImageComponent
                src={currentItem.rightIcon}
                width="16px"
                height="16px"
                alt={"rightIcon"}
              />
            </ImageContainer>
          )}
        </CarouselItemStyle>
      </CarouselPersuasionStyle>
      {showDropOffModal && (
        <DropOffModal
          isOpen={showDropOffModal}
          onClose={() => setShowDropOffModal(false)}
          data={modalData}
          onCTAClick={() => setShowDropOffModal(false)}
        />
      )}
    </>
  );
};

export default CarouselPersuasion;
